import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, Mail, Lock, AlertCircle, Shield, Key, Eye, EyeOff } from 'lucide-react';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWith2FA } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!showTwoFactor) {
        // Primer paso: login normal
        try {
          await login(email, password);
        } catch (error: any) {
          // Si el error indica que se requiere 2FA, mostrar el paso de 2FA
          if (error.requires2FA && error.tempToken) {
            setTempToken(error.tempToken);
            setShowTwoFactor(true);
            setError('');
          } else {
            setError(error.message);
          }
        }
      } else {
        // Segundo paso: login con 2FA
        await loginWith2FA(tempToken, twoFactorCode);
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTwoFactorCode('');
    setTempToken('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
      <div className="w-full max-w-md">
        {/* Logo y branding */}
        <div className="text-center mb-8">
          <div className="relative mb-6">
            <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/25">
              {showTwoFactor ? <Shield size={40} className="text-white" /> : <LogIn size={40} className="text-white" />}
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-pulse"></div>
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent mb-2">
            Agentesia Academy
          </h1>
          <p className="text-slate-200 text-lg font-medium">
            Portal de Estudiantes
          </p>
          <p className="text-slate-300 text-sm mt-1">
            {showTwoFactor ? 'Ingresa tu código de verificación' : 'Accede a tu plataforma de aprendizaje IA'}
          </p>
        </div>

        {/* Form container */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
          <div className="space-y-6">

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3 backdrop-blur-sm">
                <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={18} className="text-red-400" />
                </div>
                <span className="text-red-300 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {!showTwoFactor ? (
                // Paso 1: Email y contraseña
                <>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-100 mb-3">
                      Correo Electrónico
                    </label>
                    <div className="relative">
                      <Mail size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200 text-lg"
                        placeholder="tu@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-slate-100 mb-3">
                      Contraseña
                    </label>
                    <div className="relative">
                      <Lock size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" />
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200 text-lg"
                        placeholder="••••••••"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-300 hover:text-blue-200 transition-colors"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // Paso 2: Código 2FA
                <div>
                  <label htmlFor="twoFactorCode" className="block text-sm font-semibold text-slate-100 mb-3">
                    Código de Verificación (6 dígitos)
                  </label>
                  <div className="relative">
                    <Key size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-300" />
                    <input
                      id="twoFactorCode"
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm transition-all duration-200 text-lg text-center tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                  </div>
                  <p className="text-slate-300 text-sm mt-2">
                    Abre tu aplicación authenticator e ingresa el código de 6 dígitos.
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                {showTwoFactor && (
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="flex-1 py-4 px-6 bg-slate-600/50 text-white font-semibold rounded-xl hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-200 text-lg flex items-center justify-center gap-3"
                  >
                    ← Volver
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading || (showTwoFactor && twoFactorCode.length !== 6)}
                  className={`${showTwoFactor ? 'flex-1' : 'w-full'} py-4 px-6 bg-gradient-to-r from-blue-500 to-cyan-400 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-cyan-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/25 text-lg flex items-center justify-center gap-3`}
                >
                  {loading ? (
                    <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      {showTwoFactor ? <Shield size={20} /> : <LogIn size={20} />}
                      {showTwoFactor ? 'Verificar' : 'Iniciar Sesión'}
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* Forgot Password Link */}
            {!showTwoFactor && (
              <div className="mt-4 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-300 hover:text-white transition-colors underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            )}
          </div>

        </div>

        {/* Links adicionales */}
        {!showTwoFactor && (
          <div className="mt-8 text-center space-y-4">
            <p className="text-slate-200">
              ¿No tienes cuenta?{' '}
              <Link
                to="/register"
                className="text-blue-300 hover:text-blue-200 font-semibold transition-colors duration-200 hover:underline"
              >
                Regístrate aquí
              </Link>
            </p>
            
            <div className="flex items-center gap-4 justify-center">
              <div className="h-px bg-slate-700 flex-1"></div>
              <span className="text-slate-300 text-sm px-2">o</span>
              <div className="h-px bg-slate-700 flex-1"></div>
            </div>
            
            <a
              href="https://escuela.agentesia.cloud"
              className="inline-flex items-center gap-2 text-slate-300 hover:text-blue-300 text-sm transition-colors duration-200"
            >
              ← Volver al sitio principal
            </a>
          </div>
        )}

        {/* Tech badges */}
        <div className="mt-8 flex justify-center">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-full border border-slate-700/50">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs text-slate-300 font-medium">Plataforma Segura</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;